import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, Signal } from '@angular/core';

import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { SubmapFacetValue, SubmapNode } from '../../entities/submap-node.entity';
import {
    expandToBudget,
    isTruncated,
    SUBMAP_FACET_VALUE_COUNT,
    toAncestorKeys,
    toNodeKey,
    toNodeSegments,
    toSelectedNodeKey,
    toSubmapNodes
} from '../../helpers/submap-tree.helper';

/** A request to narrow one level of the tree, which the container turns into a `Value.Constraints.Prefix`. */
export interface SubmapLevelSearch {
    /**
     * The folders **above and including** the level being searched, root first — empty for the root level.
     *
     * ⚠ Deliberately not called `ancestors`: it holds the searched folder's own segment too, where
     * {@link SubmapNode.ancestors} stops short of it. One word with two meanings is worse than two words.
     */
    levelSegments: readonly string[];
    term: string;
}

/** One folder as the template draws it, with the three states a row can be in already resolved. */
export interface SubmapTreeRow {
    node: SubmapNode;
    key: string;
    /** The folder the active filter names. Exactly one row in the tree can be selected. */
    selected: boolean;
    /** Covered by the selection above it. Drawn as included, never as ticked — the query holds one term. */
    included: boolean;
    expandable: boolean;
    expanded: boolean;
    children: SubmapTreeRow[];
}

/**
 * HFBD-6: the **Submap** filter, a tree of the folders a dossier's stukken sit in.
 *
 * Presentational. It takes the `Dynamic.DocumentPath` facet response as the engine returns it, rolls the
 * folders and their counts up itself, and emits the values the `f` URL parameter should carry. It makes no
 * request and reads no URL, so a spec over it covers the value mapping, the counts, the selection, the row
 * budget and the truncation line without a page, a container or a browser.
 *
 * Four rules are its contract, and each of them is easy to undo by accident:
 *
 * 1. **Expanding is not filtering.** A chevron never emits anything. This is the defect the whole story
 *    exists to fix — today the informatiebeheerder has to filter in order to see what there is to filter on.
 * 2. **Selecting is a drill-down.** Ticking a node emits its own segment **plus every ancestor segment**, so
 *    `2024` under `Advies` cannot also match `Correspondentie / 2024`. `SelectionType: MUST` ANDs the terms.
 * 3. **No half-state, ever.** A parent selection is **one** query term that already includes its descendants,
 *    because the queried field is a list of segments and a term matches a segment anywhere in a path. So
 *    descendants of a selection are drawn as *included*, never as *selected*.
 * 4. **A selected node stays in the tree.** ⚠ This deliberately breaks the product's pattern where a selected
 *    value leaves the panel and lives only in its pill. That pattern exists so a flat list does not show the
 *    same thing twice; a tree row carries **position**, and the selected node is the only thing that says
 *    where the user is. The duplication is a decision, not an inconsistency to tidy away.
 *
 * ⚠ It renders **nothing** when the dossier's paths hold no folder. HFBD-5's hide-non-discriminating rule
 * cannot do that job: it judges the profile's flat *segment* facet, which on a folderless dossier holds the
 * dossier name plus every filename — many values, not one.
 */
@Component({
    standalone: true,
    selector: 'digihaven-submap-tree',
    templateUrl: './submap-tree.component.html',
    styleUrls: ['./submap-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, TranslateModule]
})
export class SubmapTreeComponent {
    /** The `Dynamic.DocumentPath` facet buckets: one whole path per bucket, never one per folder. */
    readonly values = input<readonly SubmapFacetValue[]>([]);

    /** How many values were asked for, which is the only honest way to detect truncation. */
    readonly requestedValueCount = input<number>(SUBMAP_FACET_VALUE_COUNT);

    /** The values this facet currently carries in the `f` URL parameter, in whatever order it holds them. */
    readonly activeSegments = input<readonly string[]>([]);

    /** The queried facet's key, used to make the radio inputs of one facet a single group. */
    readonly facetKey = input<string>('');

    /**
     * The whole value set this facet should carry, empty to clear it. One emission per click: the ancestor
     * chain is replaced atomically, so the query can never name a folder without its parents.
     */
    readonly selectionChanged = output<readonly string[]>();

    /** Only ever emitted above the engine's value ceiling, where nothing else can reach a hidden folder. */
    readonly searchChanged = output<SubmapLevelSearch>();

    private readonly nodes: Signal<SubmapNode[]> = computed(() => toSubmapNodes(this.values()));

    private readonly selectedKey: Signal<string | null> = computed(() =>
        toSelectedNodeKey(this.nodes(), this.activeSegments())
    );

    /**
     * The folders the budget opens, plus the ancestors of an active selection. Recomputed from the values and
     * the selection — ⚠ never persisted, and it survives neither a reload nor a navigation.
     */
    private readonly budgetOpenKeys: Signal<ReadonlySet<string>> = computed(() => {
        const selected: string | null = this.selectedKey();

        return expandToBudget(this.nodes(), selected === null ? new Set() : toAncestorKeys(selected));
    });

    /**
     * Which folders are open. `linkedSignal` is what makes a chevron a *local* override that a new result set
     * discards: the budget is the source, and a fresh one resets the overrides with it.
     */
    private readonly openKeys = linkedSignal<ReadonlySet<string>, Set<string>>({
        source: this.budgetOpenKeys,
        computation: budgetOpen => new Set(budgetOpen)
    });

    protected readonly rows: Signal<SubmapTreeRow[]> = computed(() =>
        this.toRows(this.nodes(), this.selectedKey(), this.openKeys(), false)
    );

    /**
     * ⚠ Detected by **count**, never from `HasMoreValues` — that flag reports `false` while truncating at the
     * engine's 100-value ceiling. See {@link isTruncated}.
     */
    protected readonly truncated: Signal<boolean> = computed(() =>
        isTruncated(this.values(), this.requestedValueCount())
    );

    private toRows(
        nodes: readonly SubmapNode[],
        selectedKey: string | null,
        openKeys: ReadonlySet<string>,
        underSelection: boolean
    ): SubmapTreeRow[] {
        return nodes.map(node => {
            const key: string = toNodeKey(node);
            const selected: boolean = key === selectedKey;

            return {
                node,
                key,
                selected,
                included: underSelection,
                expandable: node.children.length > 0,
                expanded: openKeys.has(key),
                children: this.toRows(node.children, selectedKey, openKeys, underSelection || selected)
            };
        });
    }

    /** ⚠ Emits nothing. A chevron reveals structure and leaves the query alone. */
    protected onToggleExpanded(row: SubmapTreeRow): void {
        const next: Set<string> = new Set(this.openKeys());

        if (next.has(row.key)) {
            next.delete(row.key);
        } else {
            next.add(row.key);
        }

        this.openKeys.set(next);
    }

    /**
     * Ticking a folder drills down to it; ticking the folder that is already selected steps all the way back
     * out. Ticking an **ancestor** of the selection steps up one level, because its own chain is shorter —
     * which is what lets the informatiebeheerder move around without clearing the filter first.
     */
    protected onToggleSelected(row: SubmapTreeRow): void {
        this.selectionChanged.emit(row.selected ? [] : toNodeSegments(row.node));
    }

    /** Takes the event rather than a value, so the template needs no `$any` to reach `target.value`. */
    protected onSearch(row: SubmapTreeRow | null, event: Event): void {
        this.searchChanged.emit({
            levelSegments: row === null ? [] : toNodeSegments(row.node),
            term: (event.target as HTMLInputElement).value
        });
    }

    protected inputId(row: SubmapTreeRow): string {
        return `submap_${this.facetKey()}_${row.key}`;
    }
}
